import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useFlowium } from '../client/FlowiumContext';
import { getDisplayLabel } from '../utils';
import Button from './Button';
import CreateProjectModal from './CreateProjectModal';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import Modal from './Modal';
import WorkspaceIcon from './WorkspaceIcon';

const MobileScopeMenu: React.FC = () => {
    const {
        client,
        currentProject,
        currentWorkspace,
        fetchUserData,
        projects,
        workspaces,
    } = useFlowium();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isCreateWorkspaceOpen, setIsCreateWorkspaceOpen] = useState(false);
    const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!currentWorkspace) return null;

    const switchWorkspace = async (workspaceId: string) => {
        if (workspaceId === currentWorkspace.id) {
            setIsOpen(false);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await client.setWorkspace(undefined, workspaceId);
            await fetchUserData();
            setIsOpen(false);
            navigate('/browser');
        } catch {
            setError('Failed to switch workspace. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const switchProject = async (projectId: string) => {
        setBusy(true);
        setError(null);
        try {
            await client.setProject(projectId, currentWorkspace.id);
            await fetchUserData();
            setIsOpen(false);
            navigate('/browser');
        } catch {
            setError('Failed to switch project. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="lg:hidden">
                <WorkspaceIcon
                    workspace={currentWorkspace}
                    isActive={false}
                    onClick={() => {
                        setError(null);
                        setIsOpen(true);
                    }}
                    className="scale-[0.86]"
                    ariaLabel="Open workspace and project navigation"
                />
            </div>

            <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title="Workspaces and projects"
                description="Choose where you want to browse and manage files."
                size="sm"
            >
                <div className="space-y-6">
                    {error && (
                        <p role="alert" className="text-sm text-error">
                            {error}
                        </p>
                    )}

                    <section aria-labelledby="mobile-workspaces-heading">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <h3 id="mobile-workspaces-heading" className="text-sm font-semibold text-content">
                                Workspaces
                            </h3>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setIsOpen(false);
                                    setIsCreateWorkspaceOpen(true);
                                }}
                            >
                                Create workspace
                            </Button>
                        </div>
                        <div className="space-y-1">
                            {workspaces.map((workspace) => {
                                const isCurrent = workspace.id === currentWorkspace.id;
                                return (
                                    <button
                                        key={workspace.id}
                                        type="button"
                                        onClick={() => switchWorkspace(workspace.id)}
                                        disabled={busy}
                                        aria-pressed={isCurrent}
                                        className={`flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-medium transition-colors ${
                                            isCurrent
                                                ? 'bg-surface-secondary text-content'
                                                : 'text-content-secondary hover:bg-hover hover:text-content'
                                        }`}
                                    >
                                        {getDisplayLabel(workspace.name, 'Untitled workspace')}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section aria-labelledby="mobile-projects-heading">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <h3 id="mobile-projects-heading" className="text-sm font-semibold text-content">
                                Projects
                            </h3>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setIsOpen(false);
                                    setIsCreateProjectOpen(true);
                                }}
                            >
                                Create project
                            </Button>
                        </div>
                        <div className="space-y-1">
                            <button
                                type="button"
                                onClick={() => switchProject('')}
                                disabled={busy}
                                aria-pressed={!currentProject}
                                className={`flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-medium transition-colors ${
                                    !currentProject
                                        ? 'bg-surface-secondary text-content'
                                        : 'text-content-secondary hover:bg-hover hover:text-content'
                                }`}
                            >
                                All workspace files
                            </button>
                            {projects.map((project) => {
                                const isCurrent = project.id === currentProject?.id;
                                return (
                                    <button
                                        key={project.id}
                                        type="button"
                                        onClick={() => switchProject(project.id)}
                                        disabled={busy}
                                        aria-pressed={isCurrent}
                                        className={`flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-medium transition-colors ${
                                            isCurrent
                                                ? 'bg-surface-secondary text-content'
                                                : 'text-content-secondary hover:bg-hover hover:text-content'
                                        }`}
                                    >
                                        {getDisplayLabel(project.name, 'Untitled project')}
                                    </button>
                                );
                            })}
                            {projects.length === 0 && (
                                <p className="px-3 py-2 text-sm text-content-secondary">
                                    No projects in this workspace.
                                </p>
                            )}
                        </div>
                    </section>
                </div>
            </Modal>

            <CreateWorkspaceModal
                isOpen={isCreateWorkspaceOpen}
                onClose={() => setIsCreateWorkspaceOpen(false)}
            />
            <CreateProjectModal
                isOpen={isCreateProjectOpen}
                onClose={() => setIsCreateProjectOpen(false)}
            />
        </>
    );
};

export default MobileScopeMenu;
